/**
 * Runtime color theming — the bridge between the user's choice in the UI and
 * the semantic color tokens of `@group24ag/flowkit-tokens`.
 *
 * Nothing here holds a hex value. The raw palettes live in the token package as
 * `--flk-palette-<name>-*`; a user choice only re-points a semantic token group
 * (e.g. `--flk-color-primary-*`) at a different palette. flowkit's components
 * read those groups directly, and the app's legacy names (`--color-greenish-*`)
 * are aliases onto them, so one override reaches both. Custom colors picked
 * from a color input are the one exception and are derived via `color-mix()`.
 */

/** Shades every palette and every semantic token group provides. **/
export const COLOR_SHADES = [25, 100, 300, 500, 700, 800] as const;

/** Raw palettes declared by the token package as `--flk-palette-<name>-*`. **/
export const COLOR_PALETTES = [
  'blue',
  'mint',
  'teal',
  'indigo',
  'orange',
  'pink',
] as const;

export type ColorPaletteName = (typeof COLOR_PALETTES)[number];

/** Slots a user can recolor. **/
export type ColorSlotKey = 'accent' | 'successful' | 'running' | 'retry';

/** Either one of the shipped palettes or a freely picked color. **/
export type ColorChoice =
  | { kind: 'palette'; palette: ColorPaletteName }
  | { kind: 'custom'; hex: string };

export interface ColorSlot {
  /** Stable identifier, also used as the localStorage key. **/
  key: ColorSlotKey;

  /** Label shown in the picker. **/
  label: string;

  /** Short explanation of what this slot affects. **/
  hint: string;

  /**
   * Semantic token groups this slot drives, without the shade suffix. The
   * first one is read back to show the current color. Add a token here to make
   * it follow the same choice.
   **/
  tokens: string[];

  /** Whether this slot also provides the translucent `-500-02` variant. **/
  translucent?: boolean;
}

/**
 * The recolorable slots. Order is the order shown in the picker.
 **/
export const COLOR_SLOTS: readonly ColorSlot[] = [
  {
    key: 'accent',
    label: 'Main color',
    hint: 'Buttons, active sidebar entry, pagination, spinner, active stepper step, builder selection, charts.',
    tokens: ['--flk-color-primary'],
    translucent: true,
  },
  {
    key: 'successful',
    label: 'Status: successful',
    hint: 'Status bubble, activity ring, status tag, met conditions, success message and completed stepper step.',
    tokens: ['--flk-color-status-successful', '--flk-color-success'],
  },
  {
    key: 'running',
    label: 'Status: running',
    hint: 'Everything indicating a process or activity that is currently running.',
    tokens: ['--flk-color-status-running'],
  },
  {
    key: 'retry',
    label: 'Status: retry',
    hint: 'Activities that are being retried after a failure.',
    tokens: ['--flk-color-status-retry'],
  },
] as const;

/** Recipes deriving the shades of a freely picked color from its base value. **/
const CUSTOM_SHADE_RECIPES: Record<number, (hex: string) => string> = {
  25: hex => `color-mix(in srgb, ${hex} 25%, #ffffff)`,
  100: hex => `color-mix(in srgb, ${hex} 50%, #ffffff)`,
  300: hex => `color-mix(in srgb, ${hex} 75%, #ffffff)`,
  500: hex => hex,
  700: hex => `color-mix(in srgb, ${hex} 55%, #141414)`,
  800: hex => `color-mix(in srgb, ${hex} 35%, #141414)`,
};

/** Resolves the CSS value a choice contributes for the given shade. **/
export function shadeValue(choice: ColorChoice, shade: number): string {
  if (choice.kind === 'palette') {
    return `var(--flk-palette-${choice.palette}-${shade})`;
  }

  return (CUSTOM_SHADE_RECIPES[shade] ?? CUSTOM_SHADE_RECIPES[500])(choice.hex);
}

/** Resolves the CSS value a choice contributes for the `-500-02` variant. **/
export function translucentValue(choice: ColorChoice): string {
  return choice.kind === 'palette'
    ? `var(--flk-palette-${choice.palette}-500-02)`
    : `color-mix(in srgb, ${choice.hex} 20%, transparent)`;
}

/** Whether the given value is a six digit hex color. **/
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

/** Narrows an unknown value to a known palette name. **/
export function isPaletteName(value: unknown): value is ColorPaletteName {
  return (
    typeof value === 'string' &&
    (COLOR_PALETTES as readonly string[]).includes(value)
  );
}
